// @ts-nocheck
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Package } from '@way/theme-host/shared/types';

/**
 * Renders a membership/pricing card for a Champs theme with configurable visuals, content, and purchase interaction.
 * @component
 * @selector app-champs-membership-card
 * @example
 *   <app-champs-membership-card
 *     [plan]="samplePackage"
 *     [planName]="'Pro Plan'"
 *     [planPrice]="'$29/MO'"
 *     [features]="['Feature A', 'Feature B']"
 *     [primaryColor]="'#ff6600'"
 *     (buyClick)="onPurchase($event)">
 *   </app-champs-membership-card>
 * @input Package plan - The full package object representing the membership plan; used when emitting purchase events and rendering plan-specific data.
 * @input number index - Optional index for ordering or conditional styling (default: 0).
 * @input string|null primaryColor - Optional override for the card's primary/accent color; falls back to a default if not provided.
 * @input string|null headingColor - Optional override for heading color; has a default fallback.
 * @input string|null paragraphColor - Optional override for paragraph/body text color; has a default fallback.
 * @input string|null headingFont - Optional override for the heading font family; defaults to 'Fester, Inter, sans-serif'.
 * @input string|null bodyFont - Optional override for the body font family; defaults to 'Inter, sans-serif'.
 * @input string planName - Display name/title of the plan.
 * @input string planPrice - Display price string (e.g., "$29/MO" or "$29/month"); used by parsing helpers to extract amount and period.
 * @input string planDescription - Short description or subtitle for the plan.
 * @input string featuresTitle - Title for the features list (default: 'Key Features:').
 * @input string[] features - List of key features to display on the card.
 * @input string buttonLabel - Label for the purchase button (default: 'Purchase Membership').
 * @input 'basic'|'premium'|'platinum' colorClass - Semantic style class to apply preset styling variations (default: 'basic').
 * @input boolean showPopularBadge - Toggle display of a "Popular" badge on the card.
 * @output EventEmitter<{plan: Package, event: Event}> buyClick - Emitted when the user clicks the purchase button; payload includes the plan and original Mouse Event. The component stops event propagation before emitting.
 *
 * Key Methods:
 * - onBuyNowClick(event: Event): Stops propagation of the click and emits buyClick with { plan, event }.
 * - getPrimaryColor(): Returns primaryColor or a default hex color when not provided.
 * - getHeadingColor(): Returns headingColor or a default color when not provided.
 * - getParagraphColor(): Returns paragraphColor or a default color when not provided.
 * - getHeadingFont(): Returns headingFont or the default heading font stack.
 * - getBodyFont(): Returns bodyFont or the default body font stack.
 * - getPriceAmount(): Parses planPrice and returns the amount portion (e.g., "$29"); robust against several common price formats.
 * - getPricePeriod(): Parses planPrice and returns the period portion if present (e.g., "/MO" or "/month").
 * - hasPricePeriod(): Boolean helper that indicates whether a price period was detected.
 *
 * Lifecycle hooks:
 * - This component does not implement any Angular lifecycle hooks (e.g., OnInit) explicitly; it is a presentational/interactive component relying on inputs and user interaction.
 */
@Component({
  selector: 'app-champs-membership-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './membership-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./membership-card.component.scss'],
})
export class ChampsMembershipCardComponent {
  @Input() plan!: Package;
  @Input() index = 0;
  @Input() primaryColor: string | null = null;
  @Input() headingColor: string | null = null;
  @Input() paragraphColor: string | null = null;
  @Input() headingFont: string | null = null;
  @Input() bodyFont: string | null = null;
  @Input() planName = '';
  @Input() planPrice = '';
  @Input() planDescription = '';
  @Input() featuresTitle = 'Key Features:';
  @Input() features: string[] = [];
  @Input() buttonLabel = 'Purchase Membership';
  @Input() colorClass: 'basic' | 'premium' | 'platinum' = 'basic';
  @Input() showPopularBadge = false;

  @Output() buyClick = new EventEmitter<{ plan: Package; event: Event }>();

  onBuyNowClick(event: Event): void {
    event.stopPropagation();
    this.buyClick.emit({ plan: this.plan, event });
  }

  getPrimaryColor(): string {
    return this.primaryColor || '#00435D';
  }

  getHeadingColor(): string {
    return this.headingColor || '#15172E';
  }

  getParagraphColor(): string {
    return this.paragraphColor || '#555963';
  }

  getHeadingFont(): string {
    return this.headingFont || 'Fester, Inter, sans-serif';
  }

  getBodyFont(): string {
    return this.bodyFont || 'Inter, sans-serif';
  }

  // Parse price to separate amount and period (e.g., "$29/MO" -> "$29" and "/MO")
  /**
   * Extracts the dollar amount portion (e.g., "$29" or "$29.99") from the component's planPrice string.
   * @example
   * etPriceAmount()
   * "$29"
   * @param {void} none - No arguments.
   * @returns {string} The parsed price amount (currency + number) or the original planPrice if no amount is found.
   **/
  getPriceAmount(): string {
    if (!this.planPrice) return '';
    // Match price pattern like "$29/MO", "$29/month", "$29.99/MO", etc.
    const match = this.planPrice.match(
      /^(\$[\d,]+(?:\.\d+)?)\s*(\/MO|\/month|\/Month|\/mo|\/MONTH)?/i
    );
    if (match && match[1]) {
      return match[1]; // Return the amount part (e.g., "$29")
    }
    // If no match, try to extract just the dollar amount
    const dollarMatch = this.planPrice.match(/^(\$[\d,]+(?:\.\d+)?)/);
    if (dollarMatch) {
      return dollarMatch[1];
    }
    return this.planPrice; // Return as-is if no match
  }

  getPricePeriod(): string {
    if (!this.planPrice) return '';
    // Match price pattern like "$29/MO" or "$29/month"
    const match = this.planPrice.match(
      /^(\$[\d,]+(?:\.\d+)?)\s*(\/MO|\/month|\/Month|\/mo|\/MONTH)/i
    );
    if (match && match[2]) {
      return match[2]; // Return the period part (e.g., "/MO")
    }
    return ''; // Return empty if no period found
  }

  hasPricePeriod(): boolean {
    return this.getPricePeriod().length > 0;
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
